import React from 'react';
import { FormattedMessage } from 'react-intl';
import { isSafari, isIE, isEdge, isFirefox, isOpera, isChrome, browserName } from '@/lib/systemHelpers/browserHelpers';
import { useTheme } from '@mui/material';

import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import LayoutServicePage from '@/components/mui/Layouts/LayoutServicePage';
import { DeviceLogo } from '@/pages/notSupported/StyledComponents';

import { ReactComponent as ChromeIcon } from '@/pages/notSupported/icons/chromeIcon.svg';
import { ReactComponent as EdgeIcon } from '@/pages/notSupported/icons/edgeIcon.svg';
import BrowserSRC from '@/pages/notSupported/icons/browser.svg';

const isKnownBrowser = [isEdge, isIE, isFirefox, isSafari, isOpera, isChrome].some((item) => item);

const NotSupported = () => {
  const { labelOptions } = useTheme();

  return (
    <LayoutServicePage sx={{ textAlign: 'center', alignItems: 'center' }}>
      <img src={labelOptions.logotype.src} alt="logo" style={{ width: 460, height: 116, margin: '60px auto 0' }} />
      <Stack gap={0} flex={1} justifyContent="center" alignItems="center" component="main">
        <Typography variant="h4">
          {!isChrome && (
            <FormattedMessage
              id="notSupportedPage.generalMessage"
              values={{ browserName: isKnownBrowser ? browserName : 'Your' }}
              defaultMessage={`${browserName} browser is not supported`}
            />
          )}
          {isChrome && (
            <FormattedMessage
              id="notSupportedPage.invalidChrome.generalMessage"
              values={{ browserName: isKnownBrowser ? browserName : 'Your' }}
              defaultMessage="You are using an old version of Chrome"
            />
          )}
        </Typography>

        <DeviceLogo src={BrowserSRC} />

        <Typography component="div" sx={{ color: 'text.secondary', mb: 3 }}>
          {!isChrome && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <FormattedMessage
                id="notSupportedPage.description.title1"
                defaultMessage="Unfortunately, we only support"
              />
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <SvgIcon sx={{ fontSize: '25px' }} component={ChromeIcon} />
                <Link href="https://www.google.com/chrome/" target="_blank" rel="noopener noreferrer">
                  Chrome
                </Link>
              </Stack>
              <FormattedMessage id="notSupportedPage.description.or" defaultMessage="or" />
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <SvgIcon sx={{ fontSize: '20px' }} component={EdgeIcon} viewBox="0 0 154 167" />
                <Link href="https://www.microsoft.com/en-us/edge/download" target="_blank" rel="noopener noreferrer">
                  Microsoft Edge
                </Link>
              </Stack>
              <FormattedMessage id="notSupportedPage.description.title2" defaultMessage="internet browser." />
            </Stack>
          )}
          {isChrome && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <FormattedMessage
                id="notSupportedPage.description.invalidChrome.title1"
                defaultMessage="Update your Chrome browser to"
              />
              <SvgIcon sx={{ fontSize: '25px' }} component={ChromeIcon} inheritViewBox />
              <Link href="https://www.google.com/chrome/" target="_blank" rel="noopener noreferrer">
                {`Chrome ${process.env.REACT_APP_CHROME_VALID_VERSION}.0`}
              </Link>
              <FormattedMessage id="notSupportedPage.description.invalidChrome.title2" defaultMessage="or later" />
            </Stack>
          )}
        </Typography>

        <Typography
          variant="h6"
          sx={{ color: 'text.secondary', maxWidth: 400, fontWeight: 'normal', textAlign: 'center' }}
        >
          {!isChrome && (
            <FormattedMessage
              id="notSupportedPage.description.message"
              defaultMessage="We are working on adding support for other browsers in the near future."
            />
          )}
          {isChrome && (
            <FormattedMessage
              id="notSupportedPage.description.invalidChrome.message"
              defaultMessage="We are working on adding support for the older versions in the near future."
            />
          )}
        </Typography>
      </Stack>
    </LayoutServicePage>
  );
};

export default NotSupported;
